import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export const AltShortcutContext = createContext(null);

// Keys the provider itself owns (Home, Back, Help) — reserved so a
// page-level useAltShortcut call can never silently shadow them.
const GLOBAL_KEYS = ['H', 'B', '?'];

export function AltShortcutProvider({ children }) {
  const [altHeld, setAltHeld] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const registryRef = useRef(new Map());
  // Mirrors registryRef so components (badge layer, help modal) re-render
  // when entries are added/removed — the Map itself is mutated in place
  // for stable identity between renders.
  const [, forceRender] = useState(0);

  const navigate = useNavigate();
  const { tenantCode } = useParams();
  const { user } = useAuth();

  const homePath = user?.is_system_admin ? '/admin/dashboard' : `/app/${tenantCode}/home`;

  // Stable ref objects for the two globally-anchored badges (Home, Help) —
  // created once so Layout.js can attach them to DOM nodes directly via
  // context, independent of the registry-seeding effect below re-running.
  const homeAnchorRef = useRef(null);
  const helpAnchorRef = useRef(null);

  const register = useCallback((key, entry) => {
    const upperKey = key.toUpperCase();
    if (process.env.NODE_ENV !== 'production' && registryRef.current.has(upperKey)) {
      console.warn(`[AltShortcut] key "${upperKey}" is already registered (label: "${registryRef.current.get(upperKey).label}") — overwriting with "${entry.label}". Pick a different key to avoid this collision.`);
    }
    registryRef.current.set(upperKey, { ...entry, key: upperKey });
    forceRender((n) => n + 1);
  }, []);

  const unregister = useCallback((key) => {
    const upperKey = key.toUpperCase();
    if (GLOBAL_KEYS.includes(upperKey)) return;
    registryRef.current.delete(upperKey);
    forceRender((n) => n + 1);
  }, []);

  // Seed the three permanent global entries once. Re-registered whenever
  // homePath changes (tenant switch, admin vs tenant context) so Alt+H
  // always points at the correct destination.
  useEffect(() => {
    registryRef.current.set('H', { key: 'H', label: 'Home', category: 'Global', ref: homeAnchorRef, onActivate: () => navigate(homePath) });
    registryRef.current.set('B', { key: 'B', label: 'Back', category: 'Global', ref: { current: null }, onActivate: () => navigate(-1) });
    registryRef.current.set('?', { key: '?', label: 'Show shortcuts', category: 'Global', ref: helpAnchorRef, onActivate: () => setHelpModalOpen(true) });
    forceRender((n) => n + 1);
  }, [navigate, homePath]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Alt') {
        setAltHeld(true);
        return;
      }
      if (e.altKey) {
        const upperKey = e.key.toUpperCase();
        const entry = registryRef.current.get(upperKey);
        if (entry) {
          e.preventDefault();
          entry.onActivate();
          setAltHeld(false);
        }
      }
    };
    const handleKeyUp = (e) => {
      if (e.key === 'Alt') setAltHeld(false);
    };
    const handleBlur = () => setAltHeld(false);

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  const value = {
    altHeld,
    registry: registryRef.current,
    register,
    unregister,
    helpModalOpen,
    setHelpModalOpen,
    homeAnchorRef,
    helpAnchorRef,
  };

  return (
    <AltShortcutContext.Provider value={value}>
      {children}
    </AltShortcutContext.Provider>
  );
}

export function useAltShortcutContext() {
  const ctx = useContext(AltShortcutContext);
  if (!ctx) throw new Error('useAltShortcutContext must be used within an AltShortcutProvider');
  return ctx;
}
